// 法術效果處理器
// Spell Effect Handlers - 即時法術、場地魔法的效果處理

import type { GameState, Card, BattlefieldPosition } from '../../../types';
import type { ImmediateAction, EffectHandler } from '../EffectTriggerSystem';
import { getLogger } from '../../../utils/logger';

const log = getLogger('SpellHandlers');

/**
 * 傷害效果處理器
 * 處理「對X造成Y傷害」
 */
export class DamageHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('傷害') || text.includes('損傷');
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 解析「對敵方造成X傷害」
    const damageMatch = effectText.match(/對(敵方|對手|你)?造成(\d+)(?:點)?傷害/);
    if (damageMatch) {
      const isEnemy = !damageMatch[1] || damageMatch[1] !== '你';
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'damage',
        value: parseInt(damageMatch[2], 10),
        targetSelector: {
          zone: 'battlefield',
          side: isEnemy ? 'enemy' : 'self',
        },
      });
    }

    // 解析「使X受到Y傷害」
    const damageMatch2 = effectText.match(/使(.+?)受到(\d+)(?:點)?傷害/);
    if (damageMatch2) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'damageTarget',
        value: parseInt(damageMatch2[2], 10),
        requiresChoice: true,
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    if (action.customHandler !== 'damage' && action.customHandler !== 'damageTarget') {
      return state;
    }

    const damage = typeof action.value === 'number' ? action.value : 0;
    if (damage === 0) return state;

    const isEnemy = action.targetSelector?.side === 'enemy';
    const activeIsPlayer1 = state.activePlayerId === state.player1.id;
    const targetIsPlayer1 = isEnemy ? !activeIsPlayer1 : activeIsPlayer1;
    const playerKey = targetIsPlayer1 ? 'player1' : 'player2';
    const player = state[playerKey];

    // 對屏障造成傷害
    const currentShield = player.shield ?? 0;
    const newShield = Math.max(0, currentShield - damage);
    log.debug(`${isEnemy ? '敵方' : '你'} 受到 ${String(damage)} 傷害, 屏障 ${String(currentShield)} -> ${String(newShield)}`);

    return {
      ...state,
      [playerKey]: {
        ...player,
        shield: newShield,
      },
    };
  }
}

/**
 * 回手效果處理器
 * 處理「使X回到持有者手牌」
 */
export class ReturnToHandHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return (text.includes('回到') && text.includes('手牌')) ||
           (text.includes('彈回') && text.includes('手牌'));
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 解析「使場上X單位回到持有者手牌」
    const returnMatch = effectText.match(/使(?:場上)?(\d*)(?:個|張)?(.+?)(?:單位)?回到(?:持有者)?手牌/);
    if (returnMatch) {
      const isEnemy = effectText.includes('敵方') || effectText.includes('對手');
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'returnToHand',
        value: returnMatch[1] ? parseInt(returnMatch[1], 10) : 1,
        targetSelector: {
          zone: 'battlefield',
          side: isEnemy ? 'enemy' : 'both',
        },
        requiresChoice: true,
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    if (action.customHandler !== 'returnToHand') return state;
    if (!action.targetCardId) {
      log.debug('No target selected');
      return state;
    }

    // 在戰場上找目標
    const targetId = action.targetCardId;
    let targetCard: Card | undefined;
    let targetPosition: BattlefieldPosition | undefined;
    let targetOwnerId: string | undefined;

    for (const side of ['player1Side', 'player2Side'] as const) {
      const sideData = state.battlefield[side];
      for (const row of ['frontLine', 'backLine'] as const) {
        const line = sideData[row];
        for (let i = 0; i < line.length; i++) {
          if (line[i].unit?.id === targetId) {
            targetCard = line[i].unit ?? undefined;
            targetOwnerId = side === 'player1Side' ? state.player1.id : state.player2.id;
            targetPosition = {
              side: side === 'player1Side' ? 'player1' : 'player2',
              row: row === 'frontLine' ? 'front' : 'back',
              column: (i + 1) as 1 | 2 | 3 | 4 | 5,
            };
            break;
          }
        }
      }
    }

    if (!targetCard || !targetOwnerId || !targetPosition) {
      log.debug('Target not found on battlefield');
      return state;
    }

    // 從戰場移除
    const sideKey = targetOwnerId === state.player1.id ? 'player1Side' : 'player2Side';
    const rowKey = targetPosition.row === 'front' ? 'frontLine' : 'backLine';
    const colIdx = targetPosition.column - 1;

    const newBattlefield = { ...state.battlefield };
    const newSide = { ...newBattlefield[sideKey] };
    const newLine = [...newSide[rowKey]];
    newLine[colIdx] = { ...newLine[colIdx], unit: null };
    newSide[rowKey] = newLine;
    newBattlefield[sideKey] = newSide;

    // 加入手牌
    const playerKey = targetOwnerId === state.player1.id ? 'player1' : 'player2';
    const player = state[playerKey];
    const newHand = [...player.hand, { ...targetCard, location: 'hand' as const }];

    log.debug(`${targetCard.id} 回到手牌`);

    return {
      ...state,
      battlefield: newBattlefield,
      [playerKey]: {
        ...player,
        hand: newHand,
      },
    };
  }
}

/**
 * 洗回牌庫效果處理器
 * 處理「使X被洗回持有者牌庫」
 */
export class ShuffleToDeckHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('洗回') && (text.includes('牌庫') || text.includes('牌堆'));
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 解析「使場上X單位被洗回持有者牌庫」
    const shuffleMatch = effectText.match(/使(?:場上)?(\d*)(?:個|張)?(.+?)(?:單位)?被?洗回(?:持有者)?牌庫/);
    if (shuffleMatch) {
      const isEnemy = effectText.includes('敵方') || effectText.includes('對手');
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'shuffleToDeck',
        value: shuffleMatch[1] ? parseInt(shuffleMatch[1], 10) : 1,
        targetSelector: {
          zone: 'battlefield',
          side: isEnemy ? 'enemy' : 'both',
        },
        requiresChoice: true,
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    if (action.customHandler !== 'shuffleToDeck') return state;
    if (!action.targetCardId) {
      log.debug('No target selected');
      return state;
    }

    // 在戰場上找目標
    const targetId = action.targetCardId;
    let targetCard: Card | undefined;
    let targetPosition: BattlefieldPosition | undefined;
    let targetOwnerId: string | undefined;

    for (const side of ['player1Side', 'player2Side'] as const) {
      const sideData = state.battlefield[side];
      for (const row of ['frontLine', 'backLine'] as const) {
        const line = sideData[row];
        for (let i = 0; i < line.length; i++) {
          if (line[i].unit?.id === targetId) {
            targetCard = line[i].unit ?? undefined;
            targetOwnerId = side === 'player1Side' ? state.player1.id : state.player2.id;
            targetPosition = {
              side: side === 'player1Side' ? 'player1' : 'player2',
              row: row === 'frontLine' ? 'front' : 'back',
              column: (i + 1) as 1 | 2 | 3 | 4 | 5,
            };
            break;
          }
        }
      }
    }

    if (!targetCard || !targetOwnerId || !targetPosition) {
      log.debug('Target not found on battlefield');
      return state;
    }

    // 從戰場移除
    const sideKey = targetOwnerId === state.player1.id ? 'player1Side' : 'player2Side';
    const rowKey = targetPosition.row === 'front' ? 'frontLine' : 'backLine';
    const colIdx = targetPosition.column - 1;

    const newBattlefield = { ...state.battlefield };
    const newSide = { ...newBattlefield[sideKey] };
    const newLine = [...newSide[rowKey]];
    newLine[colIdx] = { ...newLine[colIdx], unit: null };
    newSide[rowKey] = newLine;
    newBattlefield[sideKey] = newSide;

    // 洗入牌庫
    const playerKey = targetOwnerId === state.player1.id ? 'player1' : 'player2';
    const player = state[playerKey];
    const newDeck = [...player.deck, { ...targetCard, location: 'deck' as const }];
    // 洗牌
    for (let i = newDeck.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newDeck[i], newDeck[j]] = [newDeck[j], newDeck[i]];
    }

    log.debug(`${targetCard.id} 被洗回牌庫`);

    return {
      ...state,
      battlefield: newBattlefield,
      [playerKey]: {
        ...player,
        deck: newDeck,
      },
    };
  }
}

/**
 * 攻擊重定向處理器
 * 處理「使敵方進攻單位改為進攻...」（地震術）
 */
export class AttackRedirectHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('改為進攻') || text.includes('改變攻擊目標');
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 解析「使敵方X進攻單位改為進攻...」
    if (effectText.includes('改為進攻')) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'redirectAttack',
        requiresChoice: true,
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    if (action.customHandler !== 'redirectAttack') return state;

    // 這個效果需要在防守階段、有攻擊宣言時才有用
    // 目前簡化處理：只記錄log
    log.debug('Attack redirect effect triggered (requires combat context)');
    
    // TODO: 實作攻擊重定向邏輯（需要與戰鬥系統整合）
    return state;
  }
}

/**
 * 破壞效果處理器
 * 處理「破壞X」「消滅X」
 */
export class DestroyTargetHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return (text.includes('破壞') || text.includes('消滅')) && 
           !text.includes('被破壞') && !text.includes('被消滅');
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 解析「破壞敵方X單位」
    const destroyMatch = effectText.match(/(?:破壞|消滅)(?:敵方|對手)?(\d*)(?:個|張)?(.+?)(?:單位)?/);
    if (destroyMatch) {
      const isEnemy = effectText.includes('敵方') || effectText.includes('對手');
      actions.push({
        type: 'destroy',
        sourceCardId: '',
        value: destroyMatch[1] ? parseInt(destroyMatch[1], 10) : 1,
        targetSelector: {
          zone: 'battlefield',
          side: isEnemy ? 'enemy' : 'both',
        },
        requiresChoice: true,
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    if (action.type !== 'destroy') return state;
    if (!action.targetCardId) {
      log.debug('No target selected');
      return state;
    }

    // 在戰場上找目標
    const targetId = action.targetCardId;
    let targetCard: Card | undefined;
    let targetPosition: BattlefieldPosition | undefined;
    let targetOwnerId: string | undefined;

    for (const side of ['player1Side', 'player2Side'] as const) {
      const sideData = state.battlefield[side];
      for (const row of ['frontLine', 'backLine'] as const) {
        const line = sideData[row];
        for (let i = 0; i < line.length; i++) {
          if (line[i].unit?.id === targetId) {
            targetCard = line[i].unit ?? undefined;
            targetOwnerId = side === 'player1Side' ? state.player1.id : state.player2.id;
            targetPosition = {
              side: side === 'player1Side' ? 'player1' : 'player2',
              row: row === 'frontLine' ? 'front' : 'back',
              column: (i + 1) as 1 | 2 | 3 | 4 | 5,
            };
            break;
          }
        }
      }
    }

    if (!targetCard || !targetOwnerId || !targetPosition) {
      log.debug('Target not found on battlefield');
      return state;
    }

    // Check destroy resistance
    if (targetCard.keywords?.includes('destroy_resist')) {
      log.debug(`${targetCard.id} has destroy resistance, cannot be destroyed`);
      return state;
    }

    // 從戰場移除
    const sideKey = targetOwnerId === state.player1.id ? 'player1Side' : 'player2Side';
    const rowKey = targetPosition.row === 'front' ? 'frontLine' : 'backLine';
    const colIdx = targetPosition.column - 1;

    const newBattlefield = { ...state.battlefield };
    const newSide = { ...newBattlefield[sideKey] };
    const newLine = [...newSide[rowKey]];
    newLine[colIdx] = { ...newLine[colIdx], unit: null };
    newSide[rowKey] = newLine;
    newBattlefield[sideKey] = newSide;

    // 加入墓地
    const playerKey = targetOwnerId === state.player1.id ? 'player1' : 'player2';
    const player = state[playerKey];
    const newGraveyard = [...player.graveyard, { ...targetCard, location: 'graveyard' as const }];

    log.debug(`${targetCard.id} 被破壞`);

    return {
      ...state,
      battlefield: newBattlefield,
      [playerKey]: {
        ...player,
        graveyard: newGraveyard,
      },
    };
  }
}

/**
 * 攻擊力修改處理器
 * 處理「使X+Y攻擊力」「使X-Y攻擊力」
 * 注意：不處理已由 AttackBuffEffectHandler 處理的「使你/敵方場上所有單位+X攻擊力」
 */
export class ModifyAtkHandler implements EffectHandler {
  canHandle(text: string): boolean {
    // 必須包含攻擊力和 +/-
    if (!text.includes('攻擊力')) return false;
    if (!text.includes('+') && !text.includes('-') && !text.includes('＋') && !text.includes('－')) return false;
    
    // 排除已由 AttackBuffEffectHandler 處理的模式（「使你/敵方場上所有X+N攻擊力」）
    if (/使(你|敵方)場上所有.+\+\d+攻擊力/.test(text)) return false;
    
    return true;
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 解析「使X+Y攻擊力」
    const buffMatch = effectText.match(/使(.+?)[+＋](\d+)攻擊力/);
    if (buffMatch) {
      const isAllUnits = buffMatch[1].includes('所有') || buffMatch[1].includes('全部');
      const isEnemy = buffMatch[1].includes('敵方') || buffMatch[1].includes('對手');
      actions.push({
        type: 'buff',
        sourceCardId: '',
        value: parseInt(buffMatch[2], 10),
        targetSelector: {
          zone: 'battlefield',
          side: isEnemy ? 'enemy' : 'self',
          count: isAllUnits ? 99 : 1,
        },
        requiresChoice: !isAllUnits,
      });
    }

    // 解析「使X-Y攻擊力」
    const debuffMatch = effectText.match(/使(.+?)[-－](\d+)攻擊力/);
    if (debuffMatch) {
      const isAllUnits = debuffMatch[1].includes('所有') || debuffMatch[1].includes('全部');
      const isEnemy = debuffMatch[1].includes('敵方') || debuffMatch[1].includes('對手');
      actions.push({
        type: 'debuff',
        sourceCardId: '',
        value: parseInt(debuffMatch[2], 10),
        targetSelector: {
          zone: 'battlefield',
          side: isEnemy ? 'enemy' : 'self',
          count: isAllUnits ? 99 : 1,
        },
        requiresChoice: !isAllUnits,
      });
    }

    // 解析「你的所有單位+X攻擊力」（場地魔法常見格式）
    const auraBuffMatch = effectText.match(/你的所有(?:單位)?[+＋](\d+)攻擊力/);
    if (auraBuffMatch) {
      actions.push({
        type: 'buff',
        sourceCardId: '',
        value: parseInt(auraBuffMatch[1], 10),
        targetSelector: {
          zone: 'battlefield',
          side: 'self',
          count: 99, // 所有單位
        },
        requiresChoice: false,
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    if (action.type !== 'buff' && action.type !== 'debuff') return state;

    const modifier = typeof action.value === 'number' ? action.value : 0;
    if (modifier === 0) return state;

    const actualModifier = action.type === 'debuff' ? -modifier : modifier;
    const isEnemy = action.targetSelector?.side === 'enemy';
    const activeIsPlayer1 = state.activePlayerId === state.player1.id;
    const targetIsPlayer1 = isEnemy ? !activeIsPlayer1 : activeIsPlayer1;
    const sideKey = targetIsPlayer1 ? 'player1Side' : 'player2Side';
    const isAllUnits = (action.targetSelector?.count ?? 1) > 1;

    if (isAllUnits) {
      // 影響所有單位
      const newBattlefield = { ...state.battlefield };
      const newSide = { ...newBattlefield[sideKey] };

      const applyBuff = (line: typeof newSide.frontLine): typeof newSide.frontLine => {
        return line.map((slot) => {
          if (slot.unit) {
            return {
              ...slot,
              unit: {
                ...slot.unit,
                atkModifier: (slot.unit.atkModifier ?? 0) + actualModifier,
              },
            };
          }
          return slot;
        });
      };

      newSide.frontLine = applyBuff(newSide.frontLine);
      newSide.backLine = applyBuff(newSide.backLine);
      newBattlefield[sideKey] = newSide;

      log.debug(`所有${isEnemy ? '敵方' : '己方'}單位 ${actualModifier > 0 ? '+' : ''}${String(actualModifier)} 攻擊力`);

      return {
        ...state,
        battlefield: newBattlefield,
      };
    } else {
      // 單一目標（需要選擇）
      if (!action.targetCardId) {
        log.debug('No target selected');
        return state;
      }

      // TODO: 實作單一目標攻擊力修改
      log.debug(`Single target buff not fully implemented`);
      return state;
    }
  }
}

/**
 * 橫置效果處理器
 * 處理「使X橫置」
 */
export class TapTargetHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('橫置') && !text.includes('被橫置');
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 解析「使敵方X單位橫置」
    const tapMatch = effectText.match(/使(?:敵方|對手)?(\d*)(?:個|張)?(.+?)(?:單位)?橫置/);
    if (tapMatch) {
      const isEnemy = effectText.includes('敵方') || effectText.includes('對手');
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'tap',
        value: tapMatch[1] ? parseInt(tapMatch[1], 10) : 1,
        targetSelector: {
          zone: 'battlefield',
          side: isEnemy ? 'enemy' : 'both',
        },
        requiresChoice: true,
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    if (action.customHandler !== 'tap') return state;
    if (!action.targetCardId) {
      log.debug('No target selected');
      return state;
    }

    // 在戰場上找目標並橫置
    const targetId = action.targetCardId;
    let found = false;

    const newBattlefield = { ...state.battlefield };

    for (const sideKey of ['player1Side', 'player2Side'] as const) {
      const newSide = { ...newBattlefield[sideKey] };
      for (const rowKey of ['frontLine', 'backLine'] as const) {
        const newLine = [...newSide[rowKey]];
        for (let i = 0; i < newLine.length; i++) {
          const unit = newLine[i].unit;
          if (unit?.id === targetId) {
            newLine[i] = {
              ...newLine[i],
              unit: {
                ...unit,
                orientation: 'sideways',
              },
            };
            found = true;
            log.debug(`${targetId} 被橫置`);
            break;
          }
        }
        newSide[rowKey] = newLine;
      }
      newBattlefield[sideKey] = newSide;
      if (found) break;
    }

    if (!found) {
      log.debug('Target not found on battlefield');
      return state;
    }

    return {
      ...state,
      battlefield: newBattlefield,
    };
  }
}

/**
 * 轉正效果處理器
 * 處理「使X轉正」
 */
export class UntapHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('轉正') && !text.includes('被轉正');
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 解析「使你前排戰線所有單位轉正」
    const untapAllMatch = effectText.match(/使(你|敵方)?(前排|後排)?戰線?(所有)?單位轉正/);
    if (untapAllMatch) {
      const isSelf = !untapAllMatch[1] || untapAllMatch[1] === '你';
      const row = untapAllMatch[2] === '後排' ? 'back' : untapAllMatch[2] === '前排' ? 'front' : 'both';
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'untapAll',
        value: row,
        targetSelector: {
          zone: 'battlefield',
          side: isSelf ? 'self' : 'enemy',
        },
      });
    }

    // 解析「使場上X單位轉正」
    const untapMatch = effectText.match(/使(?:場上)?(\d*)(?:個|張)?(.+?)單位轉正/);
    if (untapMatch && !untapAllMatch) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'untap',
        value: untapMatch[1] ? parseInt(untapMatch[1], 10) : 1,
        requiresChoice: true,
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    if (action.customHandler === 'untapAll') {
      const isSelf = action.targetSelector?.side !== 'enemy';
      const activeIsPlayer1 = state.activePlayerId === state.player1.id;
      const targetIsPlayer1 = isSelf ? activeIsPlayer1 : !activeIsPlayer1;
      const sideKey = targetIsPlayer1 ? 'player1Side' : 'player2Side';
      const row = action.value as string;

      const newBattlefield = { ...state.battlefield };
      const newSide = { ...newBattlefield[sideKey] };

      const untapLine = (line: typeof newSide.frontLine): typeof newSide.frontLine => {
        return line.map((slot) => {
          if (slot.unit && slot.unit.orientation === 'sideways') {
            return {
              ...slot,
              unit: { ...slot.unit, orientation: 'upright' },
            };
          }
          return slot;
        });
      };

      if (row === 'front' || row === 'both') {
        newSide.frontLine = untapLine(newSide.frontLine);
      }
      if (row === 'back' || row === 'both') {
        newSide.backLine = untapLine(newSide.backLine);
      }

      newBattlefield[sideKey] = newSide;
      log.debug(`${isSelf ? '己方' : '敵方'}${row === 'both' ? '所有' : row === 'front' ? '前排' : '後排'}單位轉正`);

      return { ...state, battlefield: newBattlefield };
    }

    if (action.customHandler === 'untap' && action.targetCardId) {
      const targetId = action.targetCardId;
      const newBattlefield = { ...state.battlefield };

      for (const sideKey of ['player1Side', 'player2Side'] as const) {
        const newSide = { ...newBattlefield[sideKey] };
        for (const rowKey of ['frontLine', 'backLine'] as const) {
          const newLine = [...newSide[rowKey]];
          for (let i = 0; i < newLine.length; i++) {
            const unit = newLine[i].unit;
            if (unit?.id === targetId) {
              newLine[i] = {
                ...newLine[i],
                unit: { ...unit, orientation: 'upright' },
              };
              log.debug(`${targetId} 轉正`);
              newSide[rowKey] = newLine;
              newBattlefield[sideKey] = newSide;
              return { ...state, battlefield: newBattlefield };
            }
          }
        }
      }
    }

    return state;
  }
}

/**
 * 賦予關鍵字效果處理器
 * 處理「使X被賦予<關鍵字>」
 */
export class GrantKeywordHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('被賦予') && text.includes('<');
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 解析「使X被賦予<關鍵字>」
    const grantMatch = effectText.match(/使(.+?)被賦予\s*<\s*(.+?)\s*>/);
    if (grantMatch) {
      const keyword = grantMatch[2];
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'grantKeyword',
        value: keyword,
        requiresChoice: true,
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    if (action.customHandler !== 'grantKeyword') return state;
    if (!action.targetCardId) {
      log.debug('No target selected');
      return state;
    }

    const keyword = action.value as string;
    const targetId = action.targetCardId;

    const newBattlefield = { ...state.battlefield };

    for (const sideKey of ['player1Side', 'player2Side'] as const) {
      const newSide = { ...newBattlefield[sideKey] };
      for (const rowKey of ['frontLine', 'backLine'] as const) {
        const newLine = [...newSide[rowKey]];
        for (let i = 0; i < newLine.length; i++) {
          const unit = newLine[i].unit;
          if (unit?.id === targetId) {
            const keywords = unit.keywords ? [...unit.keywords] : [];
            if (!keywords.includes(keyword)) {
              keywords.push(keyword);
            }
            newLine[i] = {
              ...newLine[i],
              unit: { ...unit, keywords },
            };
            log.debug(`${targetId} 被賦予 <${keyword}>`);
            newSide[rowKey] = newLine;
            newBattlefield[sideKey] = newSide;
            return { ...state, battlefield: newBattlefield };
          }
        }
      }
    }

    return state;
  }
}

/**
 * 墓地回收處理器
 * 處理「使你墓地X回手牌」
 */
export class GraveyardToHandHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('墓地') && text.includes('回手牌');
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 解析「使你墓地1單位回手牌」
    const recoverMatch = effectText.match(/使(你|敵方)?墓地(\d*)(?:個|張)?(.+?)回手牌/);
    if (recoverMatch) {
      const isSelf = !recoverMatch[1] || recoverMatch[1] === '你';
      const cardType = recoverMatch[3].includes('魔法') ? 'spell' : 'unit';
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'graveyardToHand',
        value: recoverMatch[2] ? parseInt(recoverMatch[2], 10) : 1,
        targetSelector: {
          zone: 'graveyard',
          side: isSelf ? 'self' : 'enemy',
          filter: { type: cardType },
        },
        requiresChoice: true,
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    if (action.customHandler !== 'graveyardToHand') return state;
    if (!action.targetCardId) {
      log.debug('No target selected');
      return state;
    }

    const isSelf = action.targetSelector?.side !== 'enemy';
    const activeIsPlayer1 = state.activePlayerId === state.player1.id;
    const targetIsPlayer1 = isSelf ? activeIsPlayer1 : !activeIsPlayer1;
    const playerKey = targetIsPlayer1 ? 'player1' : 'player2';
    const player = state[playerKey];

    const targetIdx = player.graveyard.findIndex((c) => c.id === action.targetCardId);
    if (targetIdx === -1) {
      log.debug('Target not found in graveyard');
      return state;
    }

    const card = player.graveyard[targetIdx];
    const newGraveyard = [...player.graveyard];
    newGraveyard.splice(targetIdx, 1);
    const newHand = [...player.hand, { ...card, location: 'hand' as const }];

    log.debug(`${card.id} 從墓地回到手牌`);

    return {
      ...state,
      [playerKey]: {
        ...player,
        graveyard: newGraveyard,
        hand: newHand,
      },
    };
  }
}

/**
 * 墓地召喚處理器
 * 處理「使墓地X單位被召喚」
 */
export class GraveyardSummonSpellHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('墓地') && text.includes('被召喚');
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 解析「使墓地1單位被召喚」
    const summonMatch = effectText.match(/使(你|敵方)?墓地(\d*)(?:個|張)?(.+?)被召喚/);
    if (summonMatch) {
      const isSelf = !summonMatch[1] || summonMatch[1] === '你';
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'graveyardSummon',
        value: summonMatch[2] ? parseInt(summonMatch[2], 10) : 1,
        targetSelector: {
          zone: 'graveyard',
          side: isSelf ? 'self' : 'enemy',
        },
        requiresChoice: true,
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    if (action.customHandler !== 'graveyardSummon') return state;
    if (!action.targetCardId || !action.targetPosition) {
      log.debug('No target or position selected');
      return state;
    }

    const isSelf = action.targetSelector?.side !== 'enemy';
    const activeIsPlayer1 = state.activePlayerId === state.player1.id;
    const targetIsPlayer1 = isSelf ? activeIsPlayer1 : !activeIsPlayer1;
    const playerKey = targetIsPlayer1 ? 'player1' : 'player2';
    const player = state[playerKey];

    const targetIdx = player.graveyard.findIndex((c) => c.id === action.targetCardId);
    if (targetIdx === -1) {
      log.debug('Target not found in graveyard');
      return state;
    }

    const card = player.graveyard[targetIdx];
    const newGraveyard = [...player.graveyard];
    newGraveyard.splice(targetIdx, 1);

    // 放到戰場
    const pos = action.targetPosition;
    const sideKey = pos.side === 'player1' ? 'player1Side' : 'player2Side';
    const rowKey = pos.row === 'front' ? 'frontLine' : 'backLine';
    const colIdx = pos.column - 1;

    const newBattlefield = { ...state.battlefield };
    const newSide = { ...newBattlefield[sideKey] };
    const newLine = [...newSide[rowKey]];

    if (newLine[colIdx].unit) {
      log.debug('Position occupied');
      return state;
    }

    newLine[colIdx] = {
      ...newLine[colIdx],
      unit: { ...card, location: 'battlefield' as const, position: pos },
    };
    newSide[rowKey] = newLine;
    newBattlefield[sideKey] = newSide;

    log.debug(`${card.id} 從墓地被召喚`);

    return {
      ...state,
      battlefield: newBattlefield,
      [playerKey]: {
        ...player,
        graveyard: newGraveyard,
      },
    };
  }
}

/**
 * 進攻無效處理器
 * 處理「使進攻無效」
 */
export class NullifyAttackHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('進攻無效') || (text.includes('攻擊') && text.includes('無效'));
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 解析「使敵方1進攻單位進攻無效並橫放」
    if (effectText.includes('進攻無效')) {
      const shouldTap = effectText.includes('橫放') || effectText.includes('橫置');
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'nullifyAttack',
        value: shouldTap ? 'tap' : 'none',
        requiresChoice: true,
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    if (action.customHandler !== 'nullifyAttack') return state;

    // 這個效果需要在防守階段、有攻擊宣言時才有用
    // 找到攻擊單位並取消其攻擊
    if (state.attackDeclarations.length === 0) {
      log.debug('No attack declarations');
      return state;
    }

    if (!action.targetCardId) {
      log.debug('No target selected');
      return state;
    }

    // 從攻擊宣言中移除
    const newAttackDeclarations = state.attackDeclarations.filter(
      (decl) => decl.attackerId !== action.targetCardId
    );

    let newState = {
      ...state,
      attackDeclarations: newAttackDeclarations,
    };

    // 如果要橫放
    if (action.value === 'tap') {
      const newBattlefield = { ...newState.battlefield };
      for (const sideKey of ['player1Side', 'player2Side'] as const) {
        const newSide = { ...newBattlefield[sideKey] };
        for (const rowKey of ['frontLine', 'backLine'] as const) {
          const newLine = [...newSide[rowKey]];
          for (let i = 0; i < newLine.length; i++) {
            const unit = newLine[i].unit;
            if (unit?.id === action.targetCardId) {
              newLine[i] = {
                ...newLine[i],
                unit: { ...unit, orientation: 'sideways' },
              };
              newSide[rowKey] = newLine;
              newBattlefield[sideKey] = newSide;
              break;
            }
          }
        }
      }
      newState = { ...newState, battlefield: newBattlefield };
    }

    log.debug(`${action.targetCardId} 進攻無效`);
    return newState;
  }
}

/**
 * 場地破壞處理器
 * 處理「使場地卡被破壞」
 */
export class DestroyFieldHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('場地') && (text.includes('被破壞') || text.includes('破壞'));
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 解析「使1場地卡被破壞」
    if (effectText.includes('場地') && effectText.includes('破壞')) {
      const isEnemy = effectText.includes('敵方');
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'destroyField',
        targetSelector: {
          zone: 'battlefield',
          side: isEnemy ? 'enemy' : 'both',
        },
        requiresChoice: !isEnemy, // 如果不指定敵方則需要選擇
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    if (action.customHandler !== 'destroyField') return state;

    const isEnemy = action.targetSelector?.side === 'enemy';
    const activeIsPlayer1 = state.activePlayerId === state.player1.id;
    const targetIsPlayer1 = isEnemy ? !activeIsPlayer1 : activeIsPlayer1;
    const playerKey = targetIsPlayer1 ? 'player1' : 'player2';
    const player = state[playerKey];

    if (!player.fieldMagic) {
      log.debug('No field magic to destroy');
      return state;
    }

    const destroyedField = player.fieldMagic;
    const newGraveyard = [...player.graveyard, { ...destroyedField, location: 'graveyard' as const }];

    log.debug(`場地魔法 ${destroyedField.id} 被破壞`);

    return {
      ...state,
      [playerKey]: {
        ...player,
        fieldMagic: undefined,
        graveyard: newGraveyard,
      },
    };
  }
}

/**
 * 變為效果處理器
 * 處理「使單位變為此卡」或「變形」效果
 */
export class TransformHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('變為') ||
           text.includes('變形') ||
           text.includes('替換為');
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 解析「使場上1單位變為此卡」
    const transformMatch = effectText.match(/使(.+?)(?:單位)?變為(.+)/);
    if (transformMatch) {
      const isEnemy = effectText.includes('敵方');
      const isSelf = effectText.includes('你場上');

      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'transform',
        value: transformMatch[2].trim(), // 變為什麼
        requiresChoice: true,
        targetSelector: {
          zone: 'battlefield',
          side: isEnemy ? 'enemy' : isSelf ? 'self' : 'any',
        },
      });
    }

    // 解析「此卡變為X」（自我變形）
    const selfTransform = effectText.match(/此卡變為(.+)/);
    if (selfTransform) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'selfTransform',
        value: selfTransform[1].trim(),
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    if (action.customHandler === 'transform') {
      const targetId = action.targetCardId;
      const transformInto = action.value as string;

      if (!targetId) {
        log.debug('缺少目標卡牌 ID');
        return state;
      }

      // 找到目標卡牌
      let targetInfo: { card: Card; position: BattlefieldPosition; sideKey: 'player1Side' | 'player2Side' } | null = null;

      for (const sideKey of ['player1Side', 'player2Side'] as const) {
        const side = state.battlefield[sideKey];
        for (const slot of [...side.frontLine, ...side.backLine]) {
          if (slot.unit?.id === targetId) {
            targetInfo = { card: slot.unit, position: slot.position, sideKey };
            break;
          }
        }
        if (targetInfo) break;
      }

      if (!targetInfo) {
        log.debug('找不到目標卡牌');
        return state;
      }

      // 將目標送入墓地
      const ownerKey = targetInfo.card.ownerId === state.player1.id ? 'player1' : 'player2';
      let newState = {
        ...state,
        [ownerKey]: {
          ...state[ownerKey],
          graveyard: [...state[ownerKey].graveyard, {
            ...targetInfo.card,
            location: 'graveyard' as const,
            position: undefined,
          }],
        },
      };

      // 從戰場移除目標
      const newBattlefield = { ...newState.battlefield };
      const targetSideKey = targetInfo.sideKey;
      const targetColumn = targetInfo.position.column;
      const newSide = { ...newBattlefield[targetSideKey] };
      const rowKey = targetInfo.position.row === 'front' ? 'frontLine' : 'backLine';
      newSide[rowKey] = newSide[rowKey].map((slot) =>
        slot.position.column === targetColumn
          ? { ...slot, unit: null }
          : slot
      );
      newBattlefield[targetSideKey] = newSide;
      newState = { ...newState, battlefield: newBattlefield };

      // 如果有指定要變成的卡牌（如來源卡），將其放置到該位置
      // 這裡簡化處理，實際需要從手牌或來源卡獲取
      if (transformInto === '此卡' && action.sourceCardId) {
        // 找到來源卡並放置
        const sourcePlayer = state.activePlayerId === state.player1.id ? 'player1' : 'player2';
        const hand = newState[sourcePlayer].hand;
        const sourceCard = hand.find((c: Card) => c.id === action.sourceCardId);

        if (sourceCard) {
          // 從手牌移除
          newState = {
            ...newState,
            [sourcePlayer]: {
              ...newState[sourcePlayer],
              hand: hand.filter((c: Card) => c.id !== action.sourceCardId),
            },
          };

          // 放置到目標位置
          const finalBattlefield = { ...newState.battlefield };
          const finalSide = { ...finalBattlefield[targetSideKey] };
          const targetPos = targetInfo.position;
          finalSide[rowKey] = finalSide[rowKey].map((slot) =>
            slot.position.column === targetColumn
              ? {
                  ...slot,
                  unit: {
                    ...sourceCard,
                    location: 'battlefield' as const,
                    position: targetPos,
                  },
                }
              : slot
          );
          finalBattlefield[targetSideKey] = finalSide;
          newState = { ...newState, battlefield: finalBattlefield };

          log.debug(`${targetId} 變為 ${sourceCard.id}`);
        }
      }

      return newState;
    }

    if (action.customHandler === 'selfTransform') {
      // 自我變形 - 需要知道變成什麼卡
      const transformInto = action.value as string;
      log.debug(`自我變形為: ${transformInto}`);
      // TODO: 實作自我變形邏輯
      return state;
    }

    return state;
  }
}

// 匯出所有法術處理器
export const spellHandlers = {
  DamageHandler,
  ReturnToHandHandler,
  ShuffleToDeckHandler,
  AttackRedirectHandler,
  DestroyTargetHandler,
  ModifyAtkHandler,
  TapTargetHandler,
  UntapHandler,
  GrantKeywordHandler,
  GraveyardToHandHandler,
  GraveyardSummonSpellHandler,
  NullifyAttackHandler,
  DestroyFieldHandler,
  TransformHandler,
};
